import React, { useMemo } from 'react';
import { SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaChevronRight, FaShoppingCart, FaDoorOpen, FaDownload } from 'react-icons/fa';

const ProductosOpus = () => {
  // Usar useMemo para evitar recrear el array de módulos en cada render
  const modules = useMemo(() => [
    {
      id: 1,
      name: "Presupuesto Programable",
      slug: "presupuesto-programable",
      image: "/images/m1.svg",
      bgImage: "/images/modules/opus_m1/rendimiento.png",
      color: "#0066CC",
      shortDesc: "Elaboración y análisis de presupuestos de obra con precios unitarios.",
      description: "Elabora presupuestos con análisis de precios unitarios, explosión de insumos, generador de conceptos, ajustes de costos y más. El estándar en la industria de la construcción para el cálculo de costos de obra.",
      features: [
        "Análisis de precios unitarios",
        "Explosión de insumos",
        "Generador de conceptos de obra",
        "Reportes personalizados",
        "Catálogos de precios y rendimientos"
      ]
    },
    {
      id: 2,
      name: "Planeación y Control Integral",
      slug: "planeacion-control",
      image: "/images/m2.svg",
      bgImage: "/images/modules/opus_m2/cabecera_m2.jpg",
      color: "#6759FF",
      shortDesc: "Programación y seguimiento de obras con diagramas de Gantt y ruta crítica.",
      description: "Planifica y controla tus proyectos con diagramas de Gantt, ruta crítica, seguimiento de avances, programación de recursos y control financiero. La solución completa para la administración de proyectos.",
      features: [
        "Diagramas de Gantt interactivos",
        "Ruta crítica",
        "Programación de recursos",
        "Seguimiento de avances",
        "Valor ganado y proyecciones"
      ]
    },
    {
      id: 3,
      name: "Administración de Compras",
      slug: "administracion-compras",
      image: "/images/m3.svg",
      bgImage: "/images/modules/opus_m3/cabecera_m3.jpg",
      color: "#54B435",
      shortDesc: "Control de adquisiciones, almacenes e inventarios para construcción.",
      description: "Gestiona requisiciones, órdenes de compra, almacenes, inventarios y costos reales de obra. Mantén el control total de los recursos materiales y financieros de tus proyectos de construcción.",
      features: [
        "Requisiciones de materiales",
        "Órdenes de compra",
        "Control de almacén",
        "Inventarios",
        "Comparativa de costos presupuestados vs reales"
      ]
    },
    {
      id: 4,
      name: "Dependencias",
      slug: "dependencias",
      image: "/images/m4.svg",
      bgImage: "/images/modules/opus_m4/opusm4.jpg",
      color: "#EC7700",
      shortDesc: "Solución para dependencias gubernamentales y licitaciones públicas.",
      description: "Diseñado específicamente para organismos públicos, automatiza el proceso de licitación, comparativos de propuestas, bases de concurso, y cumplimiento con la normatividad vigente en obra pública.",
      features: [
        "Bases de licitación",
        "Evaluación de propuestas",
        "Cuadros comparativos",
        "Dictámenes de fallo",
        "Cumplimiento normativo"
      ]
    },
    {
      id: 5,
      name: "Cuantificación BIM",
      slug: "cuantificacion-bim",
      image: "/images/m5.svg",
      bgImage: "/images/modules/opus_m5/opusm5r.jpg",
      color: "#D81B60",
      shortDesc: "Integración con modelos BIM para cuantificación automática de volúmenes.",
      description: "Conecta tus modelos BIM con OPUS para cuantificar automáticamente volúmenes de obra. Integración directa con Revit® para generar presupuestos precisos y actualizados con cada cambio en el modelo.",
      features: [
        "Integración con Revit®",
        "Cuantificación automática",
        "Presupuestación desde modelos BIM",
        "Actualización dinámica",
        "Detección de cambios"
      ]
    }
  ], []);

  // Función memoizada para renderizar cada módulo
  const renderModule = useMemo(() => (module, index) => (
    <div key={module.id} className={`flex flex-col ${index % 2 !== 0 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 items-center`}>
      <div className="w-full md:w-2/5 relative rounded-lg overflow-hidden shadow-lg">
        <div className="relative h-64">
          <img 
            src={module.bgImage} 
            alt={`OPUS ${module.name}`} 
            className="w-full h-full object-cover object-center"
            loading="lazy"
            onError={(e) => {
              e.target.src = `/images/opus-general.jpg`;
              e.target.onerror = () => {
                const parentElement = e.target.parentElement;
                if (parentElement) {
                  const gradientDiv = document.createElement('div');
                  gradientDiv.className = 'absolute inset-0';
                  gradientDiv.style.background = `linear-gradient(135deg, ${module.color}, #003171)`;
                  parentElement.appendChild(gradientDiv);
                  e.target.style.opacity = '0';
                }
              };
            }}
          />
          {/* Capa de opacidad negra */}
          <div className="absolute inset-0 bg-black/50"></div>
          {/* Logo con círculo blanco */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative flex items-center justify-center w-32">
              <div className="absolute w-[92%] h-[92%] bg-white rounded-full shadow-lg" style={{top: '4%', left: '4%'}}></div>
              <img 
                src={module.image} 
                alt={`M${module.id}`} 
                className="relative z-20 w-full h-auto" 
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="w-full md:w-3/5">
        <div className="flex items-center mb-4">
          <div 
            className="w-10 h-10 rounded-full mr-3 flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: module.color }}
          >
            {module.id}
          </div>
          <h3 className="text-3xl font-bold text-[#1A2B39]">
            {module.name}
          </h3>
        </div>
        
        <p className="text-gray-600 text-lg mb-6">{module.description}</p>
        
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-3 text-[#1A2B39]">Características principales:</h4>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4">
            {module.features.map((feature, i) => (
              <li key={i} className="flex items-start">
                <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <Link 
            to={`/modulos/${module.slug}`}
            className="bg-[#0066CC] text-white px-5 py-2 rounded-md font-medium hover:bg-[#0055AA] transition-colors flex items-center"
          >
            Ver Módulo <FaChevronRight className="ml-2" />
          </Link>
          <a 
            href="https://www.ecosoft.com.mx/comprar" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#EC7700] text-white px-5 py-2 rounded-md font-medium hover:bg-[#D96C00] transition-colors flex items-center"
          >
            Comprar <FaShoppingCart className="ml-2" />
          </a>
          <Link 
            to="/soporte/descargas"
            className="border border-[#0066CC] text-[#0066CC] px-5 py-2 rounded-md font-medium hover:bg-gray-50 transition-colors flex items-center"
          >
            Descargar <FaDownload className="ml-2" />
          </Link>
        </div>
      </div>
    </div>
  ), []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="OPUS - Productos | ECOSOFT"
        description="Descubra toda la línea de productos OPUS para la industria de la construcción - presupuestos, planeación, compras, BIM y más."
        name="ProductosOpus"
        type="website"
      />
      
      {/* Hero Section */}
      <section className="relative h-[450px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0">
          <img
            src="/images/pOpus.jpg"
            alt="OPUS Productos Banner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="container mx-auto px-4 h-full flex items-center pt-14 relative z-10">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl font-bold mb-4">Productos OPUS</h1>
            <p className="text-xl mb-8">
              Descubra la suite completa de soluciones OPUS para la industria de la construcción.
              Desde presupuestos hasta integración BIM, tenemos las herramientas que su empresa necesita.
            </p>
            <div className="flex gap-4">
              <a 
                href="#modules"
                className="bg-white text-[#0066CC] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  const modulesSection = document.getElementById('modules');
                  if (modulesSection) {
                    modulesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Explorar Módulos <FaChevronRight className="ml-2" />
              </a>
              <Link 
                to="/contacto"
                className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white/10 transition-colors flex items-center"
              >
                Solicitar Demostración <FaDoorOpen className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Modules Overview */}
      <section id="modules" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#1A2B39]">Módulos OPUS</h2>
          
          <div className="space-y-16">
            {modules.map((module, index) => renderModule(module, index))}
          </div>
        </div>
      </section>
      
      {/* CTA Section - Simplificado */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">¿Necesita una solución completa?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Obtenga la suite completa de OPUS y aproveche la integración perfecta entre todos los módulos.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://www.ecosoft.com.mx/comprar" 
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-[#0066CC] hover:bg-[#0088FF] text-white font-medium rounded-md transition-colors flex items-center"
            >
              Comprar Suite Completa <FaShoppingCart className="ml-2" />
            </a>
            <Link 
              to="/contacto"
              className="px-8 py-3 border border-[#0066CC] text-[#0066CC] hover:bg-[#0066CC]/5 font-medium rounded-md transition-colors"
            >
              Solicitar Cotización
            </Link>
          </div>
        </div>
      </section>
      
      {/* Testimonials or partners could go here */}
    </div>
  );
};

export default ProductosOpus; 